import type {FC} from 'react'
import {useState} from 'react'
import {Alert, Box, Button, CircularProgress, Stack, Typography} from '@mui/material'

import {useConfirmMemoryMeMutation} from '@apis/aiCoreAPI'
import type {
  ConfirmMemoryResult,
  MemoryAsk,
  MemoryConfirmationVerdict,
} from '@interfaces/alpinist/memoryConfirmation'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation'

interface Props {
  conversationId: string
  messageId: string
  ask: MemoryAsk
  disabled?: boolean
}

/**
 * One line under an answer, asking whether a plant fact it relied on is right: an
 * unconfirmed one, or -- worded as "still right?" -- an established one.
 *
 * This is the whole governance mechanism for organizational memory: the second of
 * two confirmations, requested from the next colleague the claim actually affects,
 * at the moment it changed an answer they wanted. There is no review queue and no
 * admin console -- with this many users neither would be opened.
 *
 * Three answers, and "not sure" is first class: without it the only ways out are a
 * lie or ignoring the question, and it is what lets the ask be muted for a while
 * instead of repeated.
 */
const MemoryConfirmationPrompt: FC<Props> = ({conversationId, messageId, ask, disabled}) => {
  const [t] = useCustomTranslation('alpinist')
  const [confirmMemory, {isLoading}] = useConfirmMemoryMeMutation()
  const [outcome, setOutcome] = useState<ConfirmMemoryResult | null>(null)
  const [failed, setFailed] = useState(false)
  const [reopened, setReopened] = useState(false)
  // What this person picked, so a reopened prompt shows their standing answer rather
  // than three identical buttons.
  const [chosen, setChosen] = useState<MemoryConfirmationVerdict | null>(null)

  // An answered ask does not disappear. It used to render nothing at all, so somebody
  // who clicked yes watched the row vanish and was left with the model's prose -- "not
  // confirmed yet", frozen in the message -- as the only trace, by then contradicting
  // the state. What it says now comes from the SERVER's status, so it survives a reload
  // and reads the same for the person who answered and the colleague who arrives later.
  const resolved = outcome?.status ?? ask.status
  const myVerdict = chosen ?? ask.verdict
  if ((ask.answered || outcome) && !reopened) {
    // The person's OWN answer first, then the claim's status. Reading the status alone
    // told somebody who clicked YES that "it will not be used again until somebody
    // checks it" -- the disputed message -- because a colleague had marked the claim
    // wrong. It described their answer as a rejection they never made.
    const thanks =
      myVerdict === 'unsure'
        ? 'unsure'
        : myVerdict === 'yes' && resolved === 'disputed'
          ? 'agreedButDisputed'
          : resolved === 'established'
            ? 'established'
            : resolved === 'disputed'
              ? 'disputed'
              : resolved === 'retired'
                ? 'retired'
                : 'counted'
    return (
      <Alert
        severity={
          thanks === 'disputed' || thanks === 'agreedButDisputed'
            ? 'warning'
            : thanks === 'retired' || thanks === 'unsure'
              ? 'info'
              : 'success'
        }
        sx={{mt: 1}}
        // Answering in haste is ordinary, and the claim is still live -- so there has
        // to be a way back. Changing an answer is an edit, never a second vote: the
        // server keeps one answer per person, and withdrawing the only "no" frees the
        // claim again rather than leaving a mis-click suppressing it forever.
        action={
          resolved === 'retired' ? undefined : (
            <Button color="inherit" size="small" onClick={() => setReopened(true)}>
              {t('memoryConfirmation.change')}
            </Button>
          )
        }
      >
        {/* The claim itself, not only the verdict. Weeks later, "you agreed" on its own
            does not say WHAT was agreed -- and the answer sits under a long reply whose
            prose has since gone stale. */}
        <Typography variant="body2" sx={{fontStyle: 'italic', mb: 0.5}}>
          {`“${ask.claim}”`}
        </Typography>
        {t(`memoryConfirmation.thanks.${thanks}`)}
      </Alert>
    )
  }

  const answer = async (verdict: MemoryConfirmationVerdict) => {
    setFailed(false)
    try {
      setOutcome(await confirmMemory({conversationId, messageId, verdict}).unwrap())
      setChosen(verdict)
      setReopened(false)
    } catch {
      // Leave the buttons live: a failed confirmation must be retryable, and it
      // must not look as though the claim was accepted.
      setFailed(true)
    }
  }

  return (
    <Box
      sx={{
        mt: 1,
        px: 1.5,
        py: 1,
        borderLeft: 2,
        borderColor: 'divider',
        bgcolor: 'action.hover',
        borderRadius: 0.5,
      }}
      data-testid="memory-confirmation-prompt"
    >
      <Typography variant="body2" color="text.secondary">
        {/* An established claim is already agreed: the question is whether it still
            holds, and calling it unconfirmed would be wrong (AB#6846). */}
        {ask.status === 'established'
          ? ask.taught_by
            ? t('memoryConfirmation.askedEstablishedWithAuthor', {author: ask.taught_by})
            : t('memoryConfirmation.askedEstablished')
          : ask.taught_by
            ? t('memoryConfirmation.askedWithAuthor', {author: ask.taught_by})
            : t('memoryConfirmation.asked')}
      </Typography>
      <Typography variant="body2" sx={{fontStyle: 'italic', my: 0.5}}>
        {`“${ask.claim}”`}
      </Typography>
      {failed && (
        <Typography variant="caption" color="error">
          {t('memoryConfirmation.failed')}
        </Typography>
      )}
      {myVerdict && (
        // Reopened to change an answer: say which one is standing, or the three buttons
        // make somebody guess what they picked last time.
        <Typography variant="caption" color="text.secondary" sx={{display: 'block'}}>
          {t('memoryConfirmation.your_answer', {
            answer: t(`memoryConfirmation.${myVerdict}`),
          })}
        </Typography>
      )}
      {/* One colour for all three, and the shape carries the meaning: filled for the
          affirmative, outlined for the objection, text for the shrug. Warning-orange on
          "No" read as a hazard warning about the claim rather than as the answer it is.
          The standing answer is stated in words above rather than by filling a button,
          because fill is now taken. */}
      <Stack direction="row" spacing={1} sx={{mt: 0.5}} alignItems="center">
        <Button
          size="small"
          variant="contained"
          disabled={disabled || isLoading}
          onClick={() => answer('yes')}
        >
          {t('memoryConfirmation.yes')}
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={disabled || isLoading}
          onClick={() => answer('no')}
        >
          {t('memoryConfirmation.no')}
        </Button>
        <Button
          size="small"
          variant="text"
          disabled={disabled || isLoading}
          onClick={() => answer('unsure')}
        >
          {t('memoryConfirmation.unsure')}
        </Button>
        {isLoading && <CircularProgress size={14} />}
      </Stack>
    </Box>
  )
}

export default MemoryConfirmationPrompt
